'use client';
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useEffect, useState, useId } from 'react';

import { SearchOutlined } from '@ant-design/icons';
import { useSelect } from 'downshift';

import { useBreakpoint } from '@contexts/breakpoints';
import { BaseCheckIcon, DownArrowIcon, UpArrowIcon } from '@assets/icons';

import {
  Container,
  CustomIcon,
  LabelContainer,
  Option,
  Options,
  OptionsContainer,
  SearchInput,
  Separator,
} from './styled';
import { DropdownSelectProps } from './DropdownInterface';

const defaultGetKey = <T,>(x: T) => (x ? String(x) : '');

const defaultLabelRenderer = <T,>(x: T) => <label>{defaultGetKey(x)}</label>;

const enableDropdownOpening = <T,>(
  items: T[],
  condition: boolean,
  showOptionsWhenNumberItemsExceed: number
) => items.length > showOptionsWhenNumberItemsExceed && condition;

export function Dropdown<T = unknown>({
  items = [],
  getKey = defaultGetKey,
  renderLabel = defaultLabelRenderer,
  renderOption = defaultLabelRenderer,
  defaultSelectedItem,
  label,
  appearance = 'default',
  height,
  $size = 'large',
  showOptionsWhenNumberItemsExceed = 0,
  onChange,
  optionsZIndex,
  resetRef,
  customBackgroundColor,
  customColor,
  separatorColor,
  withAutoComplete = false,
  autoCompleteInputProps = {
    placeholder: '',
  },
  hideIcon = false,
  disabled = false,
  ...rest
}: DropdownSelectProps<T>) {
  const [searchText, setSearchText] = useState('');
  const [data, setData] = useState(items);
  const dropdownId = useId();

  const {
    isOpen,
    selectedItem = defaultSelectedItem,
    getToggleButtonProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
    reset,
  } = useSelect({
    id: dropdownId,
    items: data,
    itemToString: getKey,
    defaultSelectedItem,
    onSelectedItemChange: ({ selectedItem }) => onChange?.(selectedItem!),
  });
  const { xl } = useBreakpoint();

  const shouldOpen = enableDropdownOpening(
    items,
    isOpen && !disabled,
    showOptionsWhenNumberItemsExceed
  );

  useEffect(() => {
    if (resetRef) {
      resetRef.current = reset;
    }
  }, [reset, resetRef]);

  useEffect(() => {
    setData(items);
  }, [items]);

  const Arrow = shouldOpen ? UpArrowIcon : DownArrowIcon;

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);
    if (value) {
      const filteredData = items.filter((item) => {
        const key = getKey(item);
        return key.toLowerCase().includes(value.toLowerCase());
      });
      setData(filteredData);
    } else {
      setData(items);
    }
  };

  return (
    <Container
      isOpen={shouldOpen}
      displayPlaceholder={!selectedItem}
      height={height}
      $size={$size}
      $appearance={appearance}
      customColor={customColor}
      customBackgroundColor={customBackgroundColor}
      aria-label={label || undefined}
      role='combobox'
      className='dropdown-component'
      disabled={disabled}
      {...rest}
    >
      <LabelContainer
        {...getToggleButtonProps()}
        aria-labelledby='dropdown-arialabelledby'
        id='dropdown-label-container'
      >
        {renderLabel(selectedItem!, defaultLabelRenderer)}
        {hideIcon ? null : (
          <CustomIcon
            icon={Arrow}
            fontSize={20}
            paletteColor={rest.hasError ? 'tomato' : undefined}
          />
        )}
      </LabelContainer>

      <>
        <Separator
          color={separatorColor}
          visible={shouldOpen}
          withAutoComplete={withAutoComplete}
        />
        <OptionsContainer
          optionsZIndex={optionsZIndex}
          color={customColor}
          backgroundColor={customBackgroundColor}
          {...rest}
          {...getMenuProps()}
          isOpen={shouldOpen}
        >
          {shouldOpen ? (
            <div>
              {withAutoComplete && (
                <SearchInput
                  value={searchText}
                  onChange={handleOnChange}
                  {...((xl === undefined || xl) && {
                    addonBefore: <SearchOutlined />,
                  })}
                  {...autoCompleteInputProps}
                />
              )}
              <Options>
                {data && Array.isArray(data) && data.length
                  ? data.map((item, index) => (
                      <Option
                        isActive={item === selectedItem}
                        isHighlighted={highlightedIndex === index}
                        key={`${getKey(item)}${index}`}
                        {...getItemProps({ item, index })}
                      >
                        <span>{renderOption(item, defaultLabelRenderer)}</span>
                        {item === selectedItem ? (
                          <CustomIcon
                            icon={BaseCheckIcon}
                            fontSize={20}
                            paletteColor='blue600'
                          />
                        ) : null}
                      </Option>
                    ))
                  : null}
              </Options>
            </div>
          ) : null}
          {/* if you Tab from menu, focus goes on button, and it shouldn't. only happens here. */}
          <div tabIndex={0} />
        </OptionsContainer>
      </>
    </Container>
  );
}

export default Dropdown;
